/** @module The brand: a tilted card in a scanner's focus frame, plus the word mark. */
import { useId } from "react";

const HOLO: [number, string][] = [
  [0, "#4eccd3"],
  [0.35, "#b0a2fe"],
  [0.65, "#f9be78"],
  [1, "#73cd9f"],
];

/** Top-left bracket of the focus frame; the others are mirrored. */
function bracket(width: number): string {
  const inner = 8 + width;
  return `M8 34V13.72A5.72 5.72 0 0 1 13.72 8H34V${inner}H${inner}V34Z`;
}

const MIRRORS = [
  "",
  "matrix(-1 0 0 1 100 0)",
  "matrix(1 0 0 -1 0 100)",
  "matrix(-1 0 0 -1 100 100)",
];

/**
 * The symbol from the brand page. Below 64 px only the front card and two diagonal
 * brackets remain, with thicker strokes, as the design asks for small sizes.
 */
export function BrandMark({ size = 26, className = "" }: { size?: number; className?: string }) {
  const gradient = useId();
  const full = size >= 64;
  const corners = full ? [0, 1, 2, 3] : [0, 3];
  const path = bracket(full ? 9 : size < 32 ? 13 : 11);
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradient} x1="0.05" y1="0.29" x2="0.95" y2="0.71">
          {HOLO.map(([offset, color]) => (
            <stop key={offset} offset={offset} stopColor={color} />
          ))}
        </linearGradient>
      </defs>
      {full ? (
        <rect
          x="24"
          y="24"
          width="38"
          height="60"
          rx="4.56"
          ry="4.8"
          fill="currentColor"
          opacity="0.18"
          transform="rotate(-19 43 54)"
        />
      ) : null}
      <rect
        x="31"
        y="20"
        width="38"
        height="60"
        rx="4.56"
        ry="4.8"
        fill={`url(#${gradient})`}
        transform="rotate(-9 50 50)"
      />
      {corners.map((corner) => (
        <path
          key={corner}
          d={path}
          fill="currentColor"
          transform={corner === 0 ? undefined : MIRRORS[corner]}
        />
      ))}
    </svg>
  );
}

/** Symbol and word mark side by side, as in the sidebar and on the login screen. */
export function BrandLockup({
  size = 26,
  textClass = "text-[length:var(--text-xl)]",
}: {
  size?: number;
  textClass?: string;
}) {
  return (
    <span className="row gap-[10px]">
      <BrandMark size={size} />
      <span
        className={`font-(family-name:--font-display) leading-none font-[650] tracking-[-0.03em] ${textClass}`}
      >
        Póke-Scan
      </span>
    </span>
  );
}
